import React from 'react';
import LazyLoad from 'react-lazyload';
import { cx, useStyles } from '../../../lib/styles';
import theme, { Size, Color } from '../../../lib/theme';
import { matchMedia } from '../../../behaviour/ResponsiveRender';
import { useMountEffect } from '../../../lib/hooks';

import adStyles from './Ad_styles';

const { matchMedia: matchMediaValues } = theme;

type Viewport = [number | string, number];

type AdSizeDimensions = [number, number];
type AdSizeDefinition = AdSizeDimensions | string;

/** Maps viewport dimensions to the specific ad size it can display. */
interface ResponsiveAdSizes {
  viewport: Viewport;
  slot: AdSizeDefinition[];
}

/** The size an ad can be; either a single static size or a responsive size mapping. */
export interface AdSize {
  size?: AdSizeDefinition;
  sizeMapping?: ResponsiveAdSizes[];
}

/** The definition of an ad. */
export interface Ad extends AdSize {
  targeting?: object;
  adUnitPath: string;
}

export interface AdSizes {
  [key: string]: AdSize;
}

export interface Ads {
  [key: string]: Ad;
}

export interface AdProps {
  /** The ad definition to display. */
  ad: Ad;

  /**
   * Any suffix to add to the end of the ad's div ID.
   * This is required if you're using the same unit path multiple times.
   */
  adIDSuffix?: string;

  /** The space to show around the ad. */
  gutter?: Size | boolean;

  /** The initial size that the ad should have to minimise content re-flow. */
  initialSize?: [number | string, number | string];

  /** The alignment the ad should have inside its containing div. */
  align?: 'flex-start' | 'center' | 'flex-end';

  /** Whether to stick the ad at the top of the screen. */
  sticky?: boolean;

  /** Whether to only render the ad on a specific media size. */
  renderMatch?: Size;

  /**
   * Whether to match the provided render requirement on devices bigger than the provided `renderMatch`.
   *
   * @default true
   */
  renderUp?: boolean;

  /** Any additional class names to add to the container element. */
  className?: string;

  /** Whether to lazy load the ad 100 pixels before it is in view. */
  lazy?: boolean;

  /** Whether the ad has been predefined using the `AdSetup` component. */
  predefined?: boolean;

  /** Whether to render the ad using an inline script to speed up the ad request time. */
  inline?: boolean;

  /** The background colour of the ad. */
  backgroundColor?: Color | string;
}

/*
  Sizes are [width, height] in pixels.
  Fluid is a special size that will fill the container
  [1x1] is used as a placeholder to force ad unit load (overrides lazy load - GPT requires a size)
*/
export const adSizes: { [key: string]: AdSizeDefinition } = {
  video: [1, 1],
  skyScraper: [120, 600], // new unused
  wideSkyScraper: [160, 600],
  mobileLeaderboard: [320, 50],
  mediumRectangle: [300, 250],
  halfPage: [300, 600],
  largeRectangle: [336, 280], // new unused
  mobileBanner: [320, 100],//unused
  videoMobile: [320, 180],
  // nativeSmall: [320, 210],
  mobileBillboard: [320, 250],//unused
  shortBanner: [540, 50], // new unused
  nativeLarge: [580, 140], // new unused
  videoDesktop: [640, 360],
  smallBillboard: [652, 250], // new unused
  leaderboard: [728, 90],
  mediumBillboard: [728, 190],
  largeLeaderboard: [970, 90], // new unused
  billboard: [970, 250],
  fluid: 'fluid',
  nativeSmall: [210, 118],//unused
  nativeTiny: [50, 50],//unused
};

export const generateAdID = (adUnitPath: string) =>
  `starlord_ad_${adUnitPath.slice(adUnitPath.lastIndexOf('/') + 1)}`;

interface GPTProps {
  className?: string;
  initialSize?: [number | string, number | string];
  ad: Ad;
  adIDSuffix?: string;
  inline?: boolean;
  predefined?: boolean;
  renderMatch?: Size;
  renderUp?: boolean;
}

export const inlineDefineAd = (
  { 
    adUnitPath, 
    size, 
    sizeMapping = [], 
    //@ts-ignore
    targeting = {} 
  }: Ad,
  adIDSuffix = ''
) => {
  return `
    googletag
      .defineSlot(
        '${adUnitPath}', 
        ${JSON.stringify(size || (sizeMapping[0] && sizeMapping[0].slot))}, 
        '${generateAdID(adUnitPath)}${adIDSuffix}'
      )
      .defineSizeMapping(
        googletag
          .sizeMapping()
          ${sizeMapping.reduce(
            (result, { viewport, slot }) =>
              `.addSize(${JSON.stringify(viewport)}, ${JSON.stringify(
                slot
              )})\n${result}`,
            '.build()'
          )}
      )
      .setTargeting('refresh', 'true')
      .addService(googletag.pubads())
      ;
  `
};

const GPT = React.memo(
  ({
    className,
    initialSize,
    ad,
    adIDSuffix = '',
    inline,
    predefined,
    renderMatch,
    renderUp = true,
  }: GPTProps) => {
    const { adUnitPath, size, sizeMapping = [], targeting = {} } = ad;

    useMountEffect(() => {
      const matches = renderMatch ? matchMedia(renderMatch).matches : true;

      if (inline || (renderUp ? !matches : matches)) return;

      const googletag = (window['googletag'] = window['googletag'] || {
        cmd: [],
      });
      googletag.cmd.push(() => {
        if (!predefined) {
          const mapping = googletag.sizeMapping();

          for (const { viewport, slot } of sizeMapping) {
            mapping.addSize(viewport, slot);
          }

          const slot = googletag.defineSlot(
            adUnitPath,
            size || (sizeMapping[0] && sizeMapping[0].slot),
            generateAdID(adUnitPath) + adIDSuffix
          );

          if (slot) {
            slot
              .defineSizeMapping(mapping.build())
              .addService(googletag.pubads())
              .setTargeting('refresh', 'true');
          }
        }

        googletag.display(generateAdID(adUnitPath) + adIDSuffix);
      });
    });

    return (
      <>
        <div
          className={className}
          suppressHydrationWarning
          dangerouslySetInnerHTML={{
            __html: `<div
            ${
              initialSize
                ? `style="min-width: ${
                    typeof initialSize[0] === 'number'
                      ? `${initialSize[0]}px`
                      : initialSize[0]
                  }; min-height: ${
                    typeof initialSize[1] === 'number'
                      ? `${initialSize[1]}px`
                      : initialSize[1]
                  };"`
                : ''
            }
            id="${generateAdID(ad.adUnitPath) + adIDSuffix}"
          ></div>`,
          }}
        />

        {inline && (
          <script
            data-cfasync={'false'}
            dangerouslySetInnerHTML={{
              __html: `
          window.googletag = window.googletag || {cmd: []};
          window.STARLORD_INLINE_ADS_DISPLAYED = window.STARLORD_INLINE_ADS_DISPLAYED || {};
          googletag.cmd.push(function() {
            if (STARLORD_INLINE_ADS_DISPLAYED['${generateAdID(
              adUnitPath
            )}${adIDSuffix}']) return;

            ${
              renderMatch
                ? `if (${renderUp ? '!' : ''}window.matchMedia('${
                    matchMediaValues[renderMatch]
                  }').matches) return;`
                : ''
            }
            ${!predefined ? inlineDefineAd(ad, adIDSuffix) : ''}

            STARLORD_INLINE_ADS_DISPLAYED['${generateAdID(
              adUnitPath
            )}${adIDSuffix}'] = true;
            googletag.display('${generateAdID(ad.adUnitPath)}${adIDSuffix}');
          });
        `,
            }}
          />
        )}
      </>
    );
  }
);

/** A Google Publisher Tags (GPT) banner ad. */
export const Ad = (props: AdProps) => {
  const {
    ad,
    adIDSuffix,
    inline,
    lazy,
    renderMatch,
    renderUp,
    predefined,
    initialSize,
    className,
  } = props;
  const styles = useStyles(adStyles(props));

  const [Container, containerProps] = lazy
    ? [LazyLoad, { once: true, offset: 100 }]
    : [React.Fragment, {}];

  return (
    <Container {...containerProps}>
      <GPT
        className={cx('Ad', styles.container, className)}
        initialSize={initialSize}
        ad={ad}
        adIDSuffix={adIDSuffix}
        inline={inline}
        renderMatch={renderMatch}
        renderUp={renderUp}
        predefined={predefined}
      />
    </Container>
  );
};

export default Ad;
